import axios from "axios";
import * as config from '../../config/envConfig';
import { loginWithPopup, loginWithRedirect, checkRedirectLogin, refreshAccessTokenSilently, getActiveAccount, isUsingLoginRedirect } from "../Authentication/MSALHelperMethods";

/**
 * @typedef {Object} LoginModel
 * @property {string} tokenType
 * @property {string} username
 * @property {string} password
 */

/**
 * Handle logging in through TWS
 * @param {this} self 
 * @param {LoginModel} param 
 * @param {Function} onSuccess 
 * @param {Function} onFail 
 */
const handleTWSLogin = (self, param, onSuccess, onFail) => {
    axios({
        url: config.TWS_LOGIN,
        method: "post",
        ContentType: "multipart/form-data",
        data: param
    }).then(
        response => {
            if (response.data.access_token !== undefined && response.data.access_token !== null && response.data.access_token !== "") {
                sessionStorage.setItem("login_access_token", response.data.access_token);
                sessionStorage.setItem("expires_on", response.data.expires_on);
                sessionStorage.setItem("refresh_token", response.data.refresh_token);

                onSuccess(self, response.data)
            } else {
                onFail(self);
            }
        },
        (err) => onFail(err)
    );
}

/**
 * Handle logging in through MSAL
 * @param {this} self 
 * @param {LoginModel} param 
 * @param {Function} onSuccess 
 * @param {Function} onFail 
 */
const handleMSALLogin = async (self, param, onSuccess, onFail) => {

    /*
    * Redirect Login Flow doesn't work with LB. The home login page doesn't get drawn when it redirects back post MS login.
    * Only Popup Login Flow works currently
    */
    const useRedirect = isUsingLoginRedirect();
    let error = undefined;
    let accessTokenResponse = undefined;
    let userWithTennantId = param.username;

    // if logging in with redirect
    if (useRedirect) {
        loginWithRedirect(userWithTennantId, (errMess, err) => { error = errMess });

        return;
    }

    // if logging in with popup
    accessTokenResponse = await loginWithPopup(userWithTennantId, (errMess, err) => { error = errMess }, (authResponse) => { accessTokenResponse = authResponse });

    let accessTokenExists = accessTokenResponse !== undefined && accessTokenResponse.accessToken !== null && accessTokenResponse.accessToken !== "";
    let accessToken = accessTokenExists ? accessTokenResponse.accessToken : null;
    let expiresOn = accessTokenExists ? accessTokenResponse.expiresOn : null;
    let expiresOnSeconds = expiresOn !== null ? Math.floor(expiresOn.getTime() / 1000) : null;

    if (accessTokenExists) {
        sessionStorage.setItem("login_access_token", accessToken);
        sessionStorage.setItem("expires_on", expiresOnSeconds);
        sessionStorage.setItem("refresh_token", "");

        let account = accessTokenResponse.account;
        let userName = account.username;

        sessionStorage.setItem("userName", userName);

        onSuccess(self, accessTokenResponse);
    } else {
        onFail(error);
        return;
    }
}

/**
 * Refresh the access token silently using a refresh token
 * @param {string} refresh_token 
 * @returns 
 */
const handleNonMSALRefreshToken = (refresh_token) => {
    let param = {
        tokenType: "refresh_token",
        refreshToken: refresh_token
    }

    axios({
        url: config.TWS_LOGIN,
        method: "post",
        ContentType: "multipart/form-data",
        data: param
    }).then(
        function (response) {
            if (response.data.access_token !== undefined && response.data.access_token !== null && response.data.access_token !== "") {
                sessionStorage.setItem("login_access_token", response.data.access_token);
                sessionStorage.setItem("expires_on", response.data.expires_on);
                sessionStorage.setItem("refresh_token", response.data.refresh_token);
            }
        }
    );

    return sessionStorage.getItem("login_access_token");
}

/**
 * Refresh the access token silently using MSAL
 * @returns 
 */
const handleMSALRefreshToken = async () => {
    let error = undefined;

    let accessTokenResponse = await refreshAccessTokenSilently((errMess, err) => { error = errMess }, (authResponse) => { accessTokenResponse = authResponse });

    let accessTokenExists = accessTokenResponse !== undefined && accessTokenResponse.accessToken !== null && accessTokenResponse.accessToken !== "";
    let accessToken = accessTokenExists ? accessTokenResponse.accessToken : null;
    let expiresOn = accessTokenExists ? accessTokenResponse.expiresOn : null;
    let expiresOnSeconds = expiresOn !== null ? Math.floor(expiresOn.getTime() / 1000) : null;

    if (accessTokenExists) {
        sessionStorage.setItem("login_access_token", accessToken);
        sessionStorage.setItem("expires_on", expiresOnSeconds);
        sessionStorage.setItem("refresh_token", "");
    } else {
        return { error: error }
    }

    return sessionStorage.getItem("login_access_token");
}

/**
 * Get if the access token has expired
 * @returns 
 */
const checkIsExpiredToken = () => {
    let expires_on = sessionStorage.getItem("expires_on");
    let dateNow = new Date().toISOString();
    let expiresDateString = new Date(expires_on * 1000).toISOString();
    let expiresOnExists = expires_on !== null && expires_on !== undefined;
    let isExpired = dateNow >= expiresDateString && expiresOnExists;

    return isExpired;
}

/**
 * Perform the check if redirect login has just occurred.
 * @param {this} self
 * @param {function(this, AuthenticationResult)} callback
 * @param {function(Error)} error
 */
const checkAuthenticationRequest = (self, callback, error) => {
    const onError = (msg, err) => {
        error(msg);
        console.log(err);
    }

    /**
     * @param {AuthenticationResult} accessTokenResponse
     */
    const onSuccess = (accessTokenResponse) => {
        let accessTokenExists = accessTokenResponse !== undefined && accessTokenResponse.accessToken !== null && accessTokenResponse.accessToken !== "";
        let accessToken = accessTokenExists ? accessTokenResponse.accessToken : null;
        let expiresOn = accessTokenExists ? accessTokenResponse.expiresOn : null;
        let expiresOnSeconds = expiresOn !== null ? Math.floor(expiresOn.getTime() / 1000) : null;

        if (accessTokenExists) {
            sessionStorage.setItem("login_access_token", accessToken);
            sessionStorage.setItem("expires_on", expiresOnSeconds);
            sessionStorage.setItem("refresh_token", undefined);
        } else {
            console.log("Error in login with redirect");
        }

        callback(self, accessTokenResponse);
    }

    checkRedirectLogin(onError, onSuccess)
}

export default {
    handleMSALLogin,
    handleTWSLogin,
    handleNonMSALRefreshToken,
    handleMSALRefreshToken,
    checkAuthenticationRequest,
    checkIsExpiredToken,
};